import React, {forwardRef, useCallback, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useInViewport} from '../../hooks/use-in-viewport'

/**
 * Autoplaying inline video that defers its network request until it reaches the viewport.
 *
 * While off-screen the element renders with no `src`, `preload="none"` and no `autoplay`,
 * so the browser issues no media request at all — `preload` alone is not enough, because
 * an `autoplay` video is fetched regardless of the preload hint. The source is attached
 * the first time the element intersects the viewport, and playback is paused again
 * whenever it scrolls back out (unless the shopper had already paused it themselves).
 */
const Video = forwardRef(({src, preload = 'metadata', rootMargin = '200px', ...props}, ref) => {
    const videoRef = useRef(null)
    const {observeRef, isInViewport, hasBeenInViewport} = useInViewport({rootMargin})

    // Remembers whether playback was running when the video left the viewport so a video
    // the shopper paused on purpose is not restarted when it scrolls back in.
    const wasPlayingRef = useRef(true)

    const setRefs = useCallback(
        (node) => {
            videoRef.current = node
            observeRef(node)
            if (typeof ref === 'function') {
                ref(node)
            } else if (ref) {
                ref.current = node
            }
        },
        [observeRef, ref]
    )

    useEffect(() => {
        const video = videoRef.current
        if (!video || !hasBeenInViewport) return
        if (isInViewport) {
            if (wasPlayingRef.current) {
                // Rejections are expected when autoplay is blocked or the source is still
                // being selected; `onCanPlay` retries once the browser has data.
                video.play()?.catch?.(() => {})
            }
        } else {
            wasPlayingRef.current = !video.paused
            video.pause()
        }
    }, [isInViewport, hasBeenInViewport])

    // The initial play() can lose the race against source selection — retry once the
    // browser reports it has enough data.
    const handleCanPlay = useCallback(() => {
        const video = videoRef.current
        if (!video || !isInViewport || !wasPlayingRef.current || !video.paused) return
        video.play()?.catch?.(() => {})
    }, [isInViewport])

    return (
        <video
            src={hasBeenInViewport ? src : undefined}
            autoPlay={hasBeenInViewport}
            muted
            loop
            width="100%"
            height="auto"
            playsInline
            preload={hasBeenInViewport ? preload : 'none'}
            onCanPlay={handleCanPlay}
            {...props}
            ref={setRefs}
        />
    )
})

Video.propTypes = {
    src: PropTypes.string,
    preload: PropTypes.oneOf(['none', 'metadata', 'auto']),
    /** Margin around the viewport at which the source is attached and playback starts */
    rootMargin: PropTypes.string
    // `ref` is handled by forwardRef and must not be declared as a propType —
    // validating it makes React read props.ref and emit "ref is not a prop".
}

Video.displayName = 'Video'

export default Video
